/**
 * wordDiff — token-level diff for the Review Changes dialog
 * (Reader Polish Prompt 8 follow-up).
 *
 * Replaces the side-by-side truncated paragraph view with an
 * inline diff that marks added/removed words. The algorithm is a
 * standard LCS (longest common subsequence) over a regex-tokenised
 * pair, which is good enough for the narrative text we render
 * (executive summaries, study overviews, recommendations).
 *
 * Pure / side-effect-free so it's testable without mounting the
 * dialog. Exported helpers:
 *   - tokenize(text) → array of word + whitespace + punctuation
 *     tokens. Whitespace is preserved as its own token so the
 *     reconstruction matches the input verbatim.
 *   - diffWords(oldText, newText) → DiffSegment[] where each
 *     segment is { kind: 'unchanged' | 'added' | 'removed', text }.
 *
 * The LCS table is bounded by the input pair size; for typical
 * narrative pairs (≤2k words each) this runs in microseconds.
 * Larger inputs fall through to a coarser line-level fallback so
 * we don't allocate a 10k×10k table for an oversized doc.
 */

export interface DiffSegment {
  kind: 'unchanged' | 'added' | 'removed';
  text: string;
}

const MAX_TOKEN_PRODUCT = 1_500_000; // ~ (1500 × 1000) tokens before fallback

/**
 * Tokenise into words + whitespace runs + punctuation. Each match
 * group preserves the original character so re-joining the tokens
 * reproduces the input. The split intentionally does NOT collapse
 * whitespace, keeping diff output character-faithful.
 */
export function tokenize(s: string): string[] {
  if (!s) return [];
  // Match: alphanumeric word (letters/digits/_), whitespace run, or
  // any single non-word non-space character.
  const matches = s.match(/[\p{L}\p{N}_]+|\s+|[^\s\p{L}\p{N}_]/gu);
  return matches ?? [];
}

/**
 * Word-level diff over two strings. Returns a contiguous segment
 * list whose `unchanged | added | removed` markers slice the
 * original inputs into a single linear narrative suitable for
 * inline render.
 */
export function diffWords(oldText: string, newText: string): DiffSegment[] {
  const a = tokenize(oldText);
  const b = tokenize(newText);

  if (a.length === 0 && b.length === 0) return [];
  if (a.length === 0) return [{ kind: 'added', text: newText }];
  if (b.length === 0) return [{ kind: 'removed', text: oldText }];

  // Coarser fallback for very long pairs — line-level diff so the
  // user still sees the structural change without OOM-risk on the
  // n×m LCS table.
  if (a.length * b.length > MAX_TOKEN_PRODUCT) {
    return diffLines(oldText, newText);
  }

  // Build LCS length table.
  const n = a.length;
  const m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array(m + 1).fill(0));
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < m; j++) {
      lcs[i + 1]![j + 1] = a[i] === b[j]
        ? lcs[i]![j]! + 1
        : Math.max(lcs[i]![j + 1]!, lcs[i + 1]![j]!);
    }
  }

  // Walk back, emit segments. Built in reverse, then reversed at
  // end so adjacent same-kind tokens collapse cleanly.
  const out: DiffSegment[] = [];
  let i = n;
  let j = m;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      out.push({ kind: 'unchanged', text: a[i - 1]! });
      i--; j--;
    } else if (lcs[i]![j - 1]! >= lcs[i - 1]![j]!) {
      out.push({ kind: 'added', text: b[j - 1]! });
      j--;
    } else {
      out.push({ kind: 'removed', text: a[i - 1]! });
      i--;
    }
  }
  while (i > 0) { out.push({ kind: 'removed', text: a[--i]! }); }
  while (j > 0) { out.push({ kind: 'added',   text: b[--j]! }); }

  out.reverse();
  return mergeAdjacent(out);
}

/**
 * Coarse fallback: split both inputs on newlines and emit one
 * segment per line, marking exact-match lines unchanged and
 * diverging blocks as removed-then-added. Cheap, character-faithful,
 * and bounded by the line count.
 */
function diffLines(oldText: string, newText: string): DiffSegment[] {
  const a = oldText.split(/(\n)/);
  const b = newText.split(/(\n)/);
  const out: DiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      out.push({ kind: 'unchanged', text: a[i]! });
      i++; j++;
      continue;
    }
    if (i < a.length) {
      out.push({ kind: 'removed', text: a[i]! });
      i++;
    }
    if (j < b.length) {
      out.push({ kind: 'added', text: b[j]! });
      j++;
    }
  }
  return mergeAdjacent(out);
}

function mergeAdjacent(segments: DiffSegment[]): DiffSegment[] {
  const out: DiffSegment[] = [];
  for (const s of segments) {
    const last = out[out.length - 1];
    if (last && last.kind === s.kind) {
      last.text += s.text;
    } else {
      out.push({ ...s });
    }
  }
  return out;
}
